import { IAvailableDictionaryFilters } from '~/store/dictionaries/types';
import { privateAxios } from '../requestsCollection';
import { IResponse } from '../types';
import {
  IAvailableDictionary,
  IDictionariesParams,
  IDictionaryItem,
  IDictionaryType,
  IDictionaryTypes,
  INameKeyedDictionaryItem,
} from './types';

const NAME_KEYED_TYPES: string[] = [
  IDictionaryTypes.MedicaidType,
  IDictionaryTypes.ExemptionCategory,
];

const isNameKeyed = (type: string): boolean => NAME_KEYED_TYPES.includes(type);

const readItem = (item: INameKeyedDictionaryItem): IDictionaryItem => {
  const { name, ...rest } = item;
  return { ...rest, value: name };
};

const writeItem = (data: {
  value?: string;
  [key: string]: string | number | boolean | string[];
}) => {
  const { value, ...rest } = data;
  return { ...rest, name: value };
};

export const getTypes = (): IResponse<IDictionaryType[]> =>
  privateAxios({ method: 'get', url: 'dictionaries/types' });

// Get list of items of specified dictionary for menu
export const getTypeList = (
  type: string,
  params?: IDictionariesParams,
  clinicId?: number,
): IResponse<{ total: number; items: IDictionaryItem[] }> => {
  const request = privateAxios({
    method: 'get',
    url: `${clinicId ? `clinic/${clinicId}` : 'dictionaries'}/${type}/list`,
    params,
  });

  if (!isNameKeyed(type)) return request;

  return request.then(response => ({
    ...response,
    data: { ...response.data, items: response.data.items.map(readItem) },
  }));
};

// Get list of items of specified dictionary for dropdown
export const getAvailableTypeList = (
  type: string,
  clinicId?: number,
): IResponse<IAvailableDictionary[]> => {
  const request = privateAxios({
    method: 'get',
    url: `${clinicId ? `clinic/${clinicId}` : 'dictionaries'}/${type}/available-list`,
  });

  if (!isNameKeyed(type)) return request;

  return request.then(response => ({ ...response, data: response.data.map(readItem) }));
};

export const getAvailableTypeListPaginated = ({
  type,
  ...params
}: { type: string } & IAvailableDictionaryFilters): IResponse<{
  items: IAvailableDictionary[];
  total: number;
}> =>
  privateAxios(
    {
      method: 'get',
      url: `dictionaries/${type}/available-list`,
      params,
    },
    'v2/',
  );

export const archiveTypeItem = (type: string, id: number, clinicId?: number): IResponse<void> =>
  privateAxios({
    method: 'delete',
    url: `${clinicId ? `clinic/${clinicId}` : 'dictionaries'}/${type}/${id}`,
  });

export const restoreTypeItem = (type: string, id: number, clinicId?: number): IResponse<void> =>
  privateAxios({
    method: 'post',
    url: `${clinicId ? `clinic/${clinicId}` : 'dictionaries'}/${type}/${id}/recover`,
  });

export const updateTypeItem = ({
  type,
  id,
  isArchived,
  clinicId,
  ...rest
}: {
  type: string;
  id: number;
  value: string;
  isArchived: boolean;
  clinicId?: number;
  [key: string]: string | number | boolean | string[];
}): IResponse<IDictionaryItem> => {
  const request = privateAxios({
    method: 'put',
    url: `${clinicId ? `clinic/${clinicId}` : 'dictionaries'}/${type}/${id}`,
    data: isNameKeyed(type) ? writeItem(rest) : rest,
  });

  if (!isNameKeyed(type)) return request;

  return request.then(response => ({
    ...response,
    data: response.data ? readItem(response.data) : response.data,
  }));
};

export const addTypeItem = ({
  type,
  isArchived,
  clinicId,
  ...rest
}: {
  type: string;
  value: string;
  isArchived: boolean;
  clinicId?: number;
  [key: string]: string | number | boolean | string[];
}): IResponse<IDictionaryItem> => {
  const request = privateAxios({
    method: 'post',
    url: `${clinicId ? `clinic/${clinicId}` : 'dictionaries'}/${type}`,
    data: isNameKeyed(type) ? writeItem(rest) : rest,
  });

  if (!isNameKeyed(type)) return request;

  return request.then(response => ({
    ...response,
    data: response.data ? readItem(response.data) : response.data,
  }));
};
